import { useState } from "react";
import { FieldValidator } from "../config/types";
import { isFormFilled, validateField } from "../utils/validators";
import { SelectChangeEvent } from "@mui/material";

export type Field<T = string> = {
  value: T;
  isValid?: boolean;
  errorMessage?: string;
};

const generateDefaultField = <T,>(value: T): Field<T> => ({
  value: value,
  isValid: value !==''?true:false,
  errorMessage: "required",
});

export type FormField<T> = {
  [K in keyof T]: Field<T[K]>;
};

export type FormSchemaType<T> = FormField<T> & {
  submitAttempt?: boolean;
};
export const useDmsForm = <T extends Record<string, any>>(
  initialStructure: T
) => {

  type FormSchema = FormSchemaType<T>
  const createDefaultState = (structure?: T): FormSchema => {
    const fields = Object.keys(structure || {}).reduce((acc, key) => {
      const valueType = structure ? structure[key as keyof T] : undefined;
      if (valueType !== undefined) {
        acc[key as keyof T] = generateDefaultField(valueType);
      }
      return acc;
    }, {} as FormField<T>);

    return {
      ...fields,
      submitAttempt: false,
    };
  };
  const [form, updateForm] = useState<FormSchema>(
    createDefaultState(initialStructure)
  );

   /**
   * Updates the value and validation state of an input and select field.
   * 
   * @param key - The field key to update.
   * @param fieldType - Optional field validation type.
   * @returns A function to handle the change event.
   */
  const updateInputField =
    (key: keyof T, fieldType?: FieldValidator) =>
   <O,> (event: React.ChangeEvent<HTMLInputElement> |  SelectChangeEvent<typeof key> | O ) => {
      updateForm((prev) => ({
        ...prev,
        [key]: {
          value: (event as React.ChangeEvent<HTMLInputElement> |  SelectChangeEvent<typeof key>).target?.value??event,
          ...validateField((event as React.ChangeEvent<HTMLInputElement> |  SelectChangeEvent<typeof key>).target?.value??event, fieldType),
        },
      }));
    };
  /**
   * Updates a nested array field when a user inputs a value in an input field.
   * 
   * @param field - The field key.
   * @param index - The index of the array element to update.
   * @param keyValue - The key for the value to update in the array element.
   * @returns A function to handle input field change for nested arrays.
   */
  const updateNestedArrayWithInputField =
    <T, K extends keyof T>(field: K, index: number, keyValue: string) =>
    (event: React.ChangeEvent<HTMLInputElement>|   SelectChangeEvent<any>) => {
      updateForm((prev) => {
        const updatedArray = [...(prev[field].value as unknown as Array<any>)];
        updatedArray[index] = {
          ...updatedArray[index],
          [keyValue]: event.target.value,
        };
        return {
          ...prev,
          [field]: { value: updatedArray },
        };
      });
    };

  const fieldHasError = (key: keyof T) => {
    const field = form[key];
    const submitAttempt = form["submitAttempt"];
    return !field?.isValid && submitAttempt;
  };

  const getFormValues = (): T => {
    const values: { [key: string]: any } = {};
    for (const key in form) {
      if (form.hasOwnProperty(key) && form[key as keyof T].value) {
        values[key] = form[key as keyof T].value;
      }
    }
    return values as T;
  };
  const isFormValid = (
    requiredFields?: (keyof T | "submitAttempt")[] | undefined,
    excludeFields?: (keyof T | "submitAttempt")[] | undefined
  ) => isFormFilled<FormSchema>(form, requiredFields, excludeFields);


  /**
 * Handles the form submission, validates required fields, and excludes specified fields.
 * 
 * @param validation - Optional object containing validation rules.
 * @param validation.requiredFields - An optional array of field keys that are required in the form.
 * @param validation.excludeFields - An optional array of field keys to be excluded from the form data.
 * 
 * @returns An object containing:
 *   - `isValid`: A boolean indicating whether the form is valid based on the provided validation rules.
 *   - `data`: The form data retrieved by the `getFormValues` function.
 */
  const onFormSubmit = (validation?: {
    requiredFields?: (keyof T)[];
    excludeFields?: (keyof T)[];
  }): { isValid: boolean; data: T } => {
    const formValid = isFormFilled<FormSchema>(
      form,
      validation?.requiredFields,
      validation?.excludeFields
    );
    updateForm((prev) => ({ ...prev, submitAttempt: true }));
    return { isValid: formValid, data: getFormValues() };
  };

  return {
    form,
    updateForm,
    isFormValid,
    resetForm: () => {
      updateForm(createDefaultState(initialStructure));
    },
    onFormSubmit,
    fieldHasError,
    updateInputField,
    updateNestedArrayWithInputField,
    getFormValues,
    setSubmitAttempt: (attempt: boolean=true) =>
      updateForm((prev) => ({ ...prev, submitAttempt: attempt })),
    
  };
  
};
